import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export const startCheckout = async (plan) => {
  const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin, trial: false });
  window.location.href = data.checkout_url;
};

export default function BillingStart() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const plan = params.get("plan");
  useEffect(() => { if (plan) api.get(`/billing/preview/${plan}`).then((r) => setOffer(r.data)).catch(() => setErr("Offre indisponible.")); }, [plan]);
  const go = async () => { setBusy(true); setErr(""); try { await startCheckout(plan); } catch (e) { setErr(e.response?.data?.detail || "Paiement indisponible."); setBusy(false); } };
  if (!plan) return <div className="p-6 text-sm text-slate-600">Aucune offre sélectionnée. <Link to="/choisir?mode=direct" className="underline">Choisir une offre</Link></div>;
  return (
    <div className="max-w-lg mx-auto py-10" data-testid="billing-start">
      <h1 className="font-heading text-2xl font-bold">Confirmer votre offre</h1>
      {offer && (
        <div className="mt-5 bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
          <div className="flex items-baseline justify-between"><span className="font-semibold text-lg">{offer.plan.name}</span><span className="font-heading text-2xl font-extrabold">{Number(offer.plan.price).toFixed(2).replace(".", ",")} € <span className="text-sm font-normal text-slate-400">{offer.plan.price_suffix}</span></span></div>
          <p className="text-sm text-slate-600">{offer.plan.tagline}</p>
          <p className="text-sm font-medium text-blue-900 bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid="billing-terms">{offer.terms} Accès immédiat à {offer.plan.name} après confirmation du paiement.</p>
          <ul className="text-xs text-slate-500 space-y-1">{offer.flow.map((f, i) => <li key={i}>{i + 1}. {f}</li>)}</ul>
          <button onClick={() => navigate("/app/billing/pending")} className="w-full text-xs text-blue-600 underline" data-testid="billing-switch-plan">Choisir une autre offre</button>
          {err && <p className="text-sm text-red-600" data-testid="billing-error">{err}</p>}
          <Button onClick={go} disabled={busy || !offer.stripe_connected} className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="billing-checkout-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" /> Continuer vers le paiement sécurisé (Stripe{offer.stripe_connected ? "" : " — bientôt"})</>}</Button>
          <button onClick={() => navigate("/app/dashboard")} className="w-full text-xs text-slate-500">Plus tard</button>
        </div>
      )}
    </div>
  );
}

export function BillingSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "pending" });
  const sid = params.get("session_id");
  useEffect(() => {
    if (!sid) return;
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/billing/session/${sid}`);
        if (data.status === "complete") { setState({ status: "complete", sub: data.subscription }); return; }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
      } catch { setState({ status: "error" }); return; }
      if (++tries < 10) setTimeout(tick, 2000); else setState({ status: "timeout" });
    };
    tick();
  }, [sid]);
  return (
    <div className="max-w-lg mx-auto py-10 text-center" data-testid="billing-success">
      {state.status === "pending" && <><Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" /><p className="mt-3 text-sm text-slate-600">Confirmation de votre abonnement en cours…</p></>}
      {state.status === "complete" && <><CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" /><h1 className="font-heading text-2xl font-bold mt-3">Votre abonnement est activé</h1><p className="text-sm text-slate-600 mt-2" data-testid="billing-success-plan">Offre {state.sub?.plan_code} · {state.sub?.status_label}</p><p className="text-xs text-slate-500 mt-1">Vous retrouvez immédiatement votre établissement, vos audits et votre plan d'action. Résiliable à tout moment depuis Paramètres → Abonnement.</p><Button asChild className="mt-6 rounded-xl"><Link to="/app/dashboard">Accéder à Respondia</Link></Button></>}
      {["expired", "error", "timeout"].includes(state.status) && <><XCircle className="w-10 h-10 mx-auto text-amber-500" /><p className="mt-3 text-sm text-slate-600">Nous n'avons pas encore la confirmation de Stripe. Votre abonnement sera mis à jour automatiquement dès réception.</p><Button asChild variant="outline" className="mt-6 rounded-xl"><Link to="/app/settings">Voir mon abonnement</Link></Button></>}
    </div>
  );
}

export function BillingCancel() {
  useEffect(() => { toast.info("Paiement annulé — aucun abonnement créé."); api.post("/track/event", { name: "checkout_abandoned", props: { reason: "cancel_url" } }).catch(() => {}); }, []);
  return (
    <div className="max-w-lg mx-auto py-10 text-center" data-testid="billing-cancel">
      <XCircle className="w-10 h-10 mx-auto text-slate-400" />
      <h1 className="font-heading text-2xl font-bold mt-3">Votre abonnement n'est pas encore activé</h1>
      <p className="text-sm text-slate-600 mt-2">Aucun abonnement n'a été créé et aucune carte n'a été débitée. Votre compte et vos données sont conservés : choisissez une offre quand vous le souhaitez.</p>
      <div className="flex flex-col sm:flex-row gap-2 justify-center mt-6">
        <Button asChild className="rounded-xl bg-blue-600 hover:bg-blue-700"><Link to="/app/billing/pending" data-testid="cancel-resume-link">Choisir une offre</Link></Button>
        <Button asChild variant="outline" className="rounded-xl"><Link to="/app/dashboard">Retour à mon espace</Link></Button>
      </div>
    </div>
  );
}
